// Draws Bezier SVG curves between step icons in the narrative snake layout.
// Reads rendered icon positions and creates smooth S-curves that flow from one
// step to the next. Hides connectors on mobile when the layout stacks vertically.

import { Controller } from "@hotwired/stimulus"

// Debounce delay for window resize redraw
const RESIZE_DEBOUNCE_MS = 150

// Icons within this horizontal distance (px) are considered stacked (mobile layout)
const MOBILE_X_THRESHOLD = 10

// Connector stroke color (matches the step icon accent)
const CONNECTOR_COLOR = '#27e8a7'

export default class extends Controller {
  connect() {
    this.svg = null
    this.resizeTimeout = null

    this.boundRedraw = this.redraw.bind(this)
    window.addEventListener('resize', this.boundRedraw)

    this.draw()
  }

  disconnect() {
    window.removeEventListener('resize', this.boundRedraw)

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout)
      this.resizeTimeout = null
    }

    this.removeSvg()
  }

  redraw() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout)
    }

    this.resizeTimeout = setTimeout(() => {
      this.resizeTimeout = null
      this.draw()
    }, RESIZE_DEBOUNCE_MS)
  }

  draw() {
    this.removeSvg()

    const icons = this.element.querySelectorAll('.step-icon')
    const steps = this.element.querySelectorAll('.narrative-step')
    if (icons.length < 2) return

    const containerRect = this.element.getBoundingClientRect()
    const iconPoints = Array.from(icons).map(icon => {
      const rect = icon.getBoundingClientRect()
      return {
        x: rect.left + rect.width / 2 - containerRect.left,
        bottom: rect.bottom - containerRect.top,
        top: rect.top - containerRect.top
      }
    })

    if (this.isMobileLayout(iconPoints)) return

    const stepRects = Array.from(steps).map(step => {
      const rect = step.getBoundingClientRect()
      return {
        bottom: rect.bottom - containerRect.top,
        top: rect.top - containerRect.top
      }
    })

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('class', 'narrative-connector-svg')
    svg.setAttribute('width', containerRect.width)
    svg.setAttribute('height', containerRect.height)

    // Blur filter for the feathered glow layer
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
    const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter')
    filter.setAttribute('id', 'connector-glow')
    filter.setAttribute('x', '-50%')
    filter.setAttribute('y', '-50%')
    filter.setAttribute('width', '200%')
    filter.setAttribute('height', '200%')
    const blur = document.createElementNS('http://www.w3.org/2000/svg', 'feGaussianBlur')
    blur.setAttribute('stdDeviation', '4')
    filter.appendChild(blur)
    defs.appendChild(filter)
    svg.appendChild(defs)

    for (let i = 0; i < iconPoints.length - 1; i++) {
      const start = iconPoints[i]
      const end = iconPoints[i + 1]

      const startY = start.bottom
      const endY = end.top

      // Route through the empty gap between step containers
      const gapMidY = (stepRects[i].bottom + stepRects[i + 1].top) / 2
      const midX = (start.x + end.x) / 2

      // Two quadratic Beziers meeting at the gap centre:
      // First half goes vertical from icon, curves into horizontal at gap
      // Second half curves from horizontal back to vertical into next icon
      const d = `M ${start.x} ${startY} Q ${start.x} ${gapMidY}, ${midX} ${gapMidY} Q ${end.x} ${gapMidY}, ${end.x} ${endY}`

      // Glow layer: wide, blurred, soft edges
      const glow = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      glow.setAttribute('d', d)
      glow.setAttribute('stroke', CONNECTOR_COLOR)
      glow.setAttribute('stroke-width', '18')
      glow.setAttribute('fill', 'none')
      glow.setAttribute('opacity', '0.08')
      glow.setAttribute('filter', 'url(#connector-glow)')
      svg.appendChild(glow)

      // Bright centre line — thin and subtle
      const core = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      core.setAttribute('d', d)
      core.setAttribute('stroke', CONNECTOR_COLOR)
      core.setAttribute('stroke-width', '2')
      core.setAttribute('fill', 'none')
      core.setAttribute('opacity', '0.35')
      svg.appendChild(core)
    }

    this.element.prepend(svg)
    this.svg = svg
  }

  removeSvg() {
    if (this.svg) {
      this.svg.remove()
      this.svg = null
    }
  }

  isMobileLayout(points) {
    if (points.length < 2) return false

    const firstX = points[0].x
    return points.every(p => Math.abs(p.x - firstX) < MOBILE_X_THRESHOLD)
  }
}
